import { VisualBrick, registerBrick, Brick, BrickContext, Config, CloudObject, ListDef, Color, Transaction, onDestroy, QueryResult } from 'olympe';
import { getLogger } from '@olympeio/core';
import { GPSCoordinates } from '@olympeio-extensions/commons';
import { combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import mapboxgl from 'mapbox-gl';
import { MapboxCamera } from './MapboxCamera';

export default class MapboxMap extends VisualBrick {

    /**
     * @override
     */
    init(context) {
        const head = document.getElementsByTagName('head')[0];

        const mapboxId = 'mapbox-style';
        if (!document.getElementById(mapboxId)) {
            const mapBoxStyle = document.createElement('link');
            mapBoxStyle.id = mapboxId;
            mapBoxStyle.rel = 'stylesheet';
            mapBoxStyle.type = 'text/css';
            mapBoxStyle.href = 'https://api.mapbox.com/mapbox-gl-js/v2.5.0/mapbox-gl.css';
            mapBoxStyle.media = 'all';
            head.appendChild(mapBoxStyle);
        }
    }

    /**
     * This method runs when the brick is ready in the HTML DOM.
     * @override
     * @param {!BrickContext} context
     * @param {!Element} elementDom
     */
    draw(context, elementDom) {
        let destroyed = false;
        context.onDestroy(() => destroyed = true);

        // Map initialization
        const mapIdentifier = 'mapbox-' + Math.round(Math.random() * Date.now());
        const mapContainer = document.createElement('div');

        mapContainer.setAttribute('style', 'position:relative;height:100%;width:100%');
        mapContainer.setAttribute('id', mapIdentifier);
        // For some reason since 1.16 migration mapbox doesn't find the div in the dom when constructing the map
        // So adding the setTimeout here introduce asynchronism that allows the DOM to have this div before calling mapboxgl.Map
        requestAnimationFrame(() => {
            elementDom.appendChild(mapContainer);

            mapboxgl.accessToken = Config.getParameter('mapbox.api-key');
            const map = new mapboxgl.Map({
                container: mapIdentifier,
                center: [6.6322734, 46.5196535],
                style: 'mapbox://styles/mapbox/streets-v11',
                zoom: context.get('Zoom') ?? 10,
            });

            // Properties Handling
            context.observe('Width').subscribe(_width => {
                map.resize();
            });

            context.observe('Hidden').subscribe(_hidden => {
                map.resize();
            });

            context.observe('Height').subscribe(_height => {
                map.resize();
            });

            context.observe('zoom').subscribe(zoom => {
                map.setZoom(zoom);
            });

            context.observe('pitch').subscribe(pitch => {
                map.setPitch(pitch);
            });

            context.observe('bearing').subscribe(bearing => {
                map.setBearing(bearing);
            });

            context.observe('style').subscribe(style => {
                map.setStyle(style);

                // Delay this to let the time to the map to load its new style before applying terrain
                setTimeout(this.renderTerrain.bind(this, context, map), 1000);
            });

            context.observe('interactive').subscribe(interactive => {
                this.handleInteractivity(interactive, map);
            });

            context.observe('center').subscribe(gpsCoord => {
                if (CloudObject.exists(gpsCoord)) {
                    combineLatest([
                        gpsCoord.observe(context, GPSCoordinates.latitudeProp),
                        gpsCoord.observe(context, GPSCoordinates.longitudeProp)
                    ]).subscribe(([lat, long]) => {
                        map.setCenter([long, lat]);
                    });
                }
            });

            context.observe('camera').subscribe((gpsCoord) => {
                if (CloudObject.exists(gpsCoord)) {
                    this.handleCamera(context, gpsCoord, map);
                }
            });

            map.on('load', () => {
                // render until container is found
                const intervalId = setInterval(() => this.resizeCanvas(map, mapContainer, intervalId), 50);

                this.setCenterProperty(context, map);

                if (!destroyed) {
                    context.observe('3D').subscribe(withTerrain => {
                        context.set('withTerrain', withTerrain);
                        this.renderTerrain(context, map);
                    });
                }
            });

            const listeners = ['dragend', 'zoomend', 'rotateend', 'pitchend', 'boxzoomend', 'moveend'];
            listeners.forEach(event => {
                map.on(event, () => {
                    this.setCenterProperty(context, map);
                });
            });

            context.observe('markers').subscribe(markers => {
                this.handleMarkers(markers, map, context);
            });

            context.observe('Center Map On Markers').subscribe(onCenterClick => {
                const handlers = context.get('handlers');
                const lngLats = [];
                if (handlers.size > 0) {
                    for (const [key, marker] of handlers) {
                        const lngLat = marker.getLngLat();
                        lngLats.push([lngLat.lng, lngLat.lat]);
                    }
                    // Create a 'LngLatBounds' with the first coordinate.
                    const bounds = new mapboxgl.LngLatBounds(lngLats[0], lngLats[0]);
                    // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
                    for (const coord of lngLats) {
                        bounds.extend(coord);
                    }
                    map.fitBounds(bounds, {padding: 24});
                }
            });
        });
    }

    /**
     * Set center as output property
     *
     * @param context
     * @param map
     */
    setCenterProperty(context, map) {
        const properties = new Map();
        properties.set(GPSCoordinates.latitudeProp, map.getCenter().lat);
        properties.set(GPSCoordinates.longitudeProp, map.getCenter().lng);

        const transaction = new Transaction(false);
        const centerTag = map.centerTag || transaction.create(GPSCoordinates, properties);

        if (map.centerTag) {
            transaction.multiUpdate(centerTag, properties);
        }

        transaction.execute().then(() => {
            if (!map.centerTag) {
                map.centerTag = centerTag;
            }
            // if we provide a new GPS Coordinates to center in runtime
            // context.getProperty('center') will refer to it, instead of actual map center
            context.set('center', CloudObject.get(centerTag));
        }).catch((message) => {
            throw `Center property was not updated. Error: ${message}`;
        });
    }

    /**
     * Resize canvas when map container is rendered
     *
     * @param map
     * @param mapContainer
     * @param intervalId
     */
    resizeCanvas(map, mapContainer, intervalId) {
        if (mapContainer.offsetHeight > 0) {
            clearInterval(intervalId);
            map.resize();
        }
    }

    /**
     * Display the markers on the map
     *
     * @param {Array | QueryResult | ListDef} instances
     * @param map
     * @param {BrickContext} context
     */
    handleMarkers(instances, map, context) {
        // If the property itself changed, remove the existing markers
        const handlers = context.get('handlers') || new Map();

        for (const marker of handlers.values()) {
            marker.remove();
        }
        handlers.clear();

        // Generate the lambda context in if there is an onItemClick function.
        const onItemClickBrick = context.get('On Item Click');
        let onItemClick = null;
        if (onItemClickBrick instanceof Brick) {
            const [ctrlFlowInput, selectedObject] = onItemClickBrick.getInputs();
            const onItemClick$ = context.runner(onItemClickBrick);
            onItemClick = (object) => {
                onItemClick$.set(selectedObject, object).set(ctrlFlowInput, Date.now());
            };
        }

        const instanceToMarker = (item) => {
            const markerObject = this.processObjectToMarker(item.getTag(), context);
            if (markerObject) {
                this.handleMarker(markerObject, map, context, handlers, onItemClick);
            }
        };

        if (instances instanceof Array) {
            instances.forEach(instanceToMarker);
        } else if (instances instanceof QueryResult) {
            instances.forEach(instanceToMarker);
        } else if (instances instanceof ListDef) {
            instances.forEach(item => {
                instanceToMarker(item);
                onDestroy(() => {
                    handlers.get(item.getTag())?.remove();
                    handlers.delete(item.getTag());
                });
            });
        } else {
            getLogger('MapboxMap').error('The markers object is supposed to be a list');
        }
        context.set('handlers', handlers);
    }


    /**
     * Transform an object to a marker
     *
     * @param key
     * @param context
     */
    processObjectToMarker(key, context) {
        const marker = { key };

        context.observe('Transform Object to Marker').subscribe(lambdaFunc => {
            const [currentObject] = lambdaFunc?.getInputs();
            const [
                latitudeOutput, longitudeOutput, popupTextOutput,
                iconOutput, iconSizeOutput, colorOutput, rotationOutput
            ] = lambdaFunc?.getOutputs();

            const lambdaContext = context.createChild('marker' + key);
            lambdaContext.set(currentObject, key);
            lambdaFunc?.run(lambdaContext);

            combineLatest([
                lambdaContext.observe(latitudeOutput),
                lambdaContext.observe(longitudeOutput),
                lambdaContext.observe(popupTextOutput).pipe(startWith('')),
                lambdaContext.observe(iconOutput).pipe(startWith('\uf041')),
                lambdaContext.observe(iconSizeOutput).pipe(startWith(24)),
                lambdaContext.observe(colorOutput).pipe(startWith(Color.create(128, 128, 128, 1))),
                lambdaContext.observe(rotationOutput).pipe(startWith(0))
            ]).subscribe(([latitude, longitude, popupText, icon, iconSize, color, rotation]) => {
                marker.lat = latitude;
                marker.long = longitude;
                marker.popupText = popupText;
                marker.iconSymbol = icon;
                marker.size = iconSize;
                marker.color = color;
                marker.rotation = rotation;
            });
        });
        return marker;
    }

    /**
     * Add a single marker on the map
     *
     * @param marker
     * @param map
     * @param {BrickContext} context
     * @param {Map} handlers
     * @param {function(CloudObject)} onClick
     */
    handleMarker(marker, map, context, handlers, onClick) {
        const { key, lat, long, color, size, rotation, iconSymbol, popupText } = marker;

        if (lat && long) {
            let mapboxMarker ;
            if (iconSymbol !== '') {
                const newElem = document.createElement('div');
                newElem.style.width = size + 'px';
                newElem.style.textShadow = '-1px 1px 3px #000000';
                newElem.style.height = size + 'px';

                const icon = document.createElement('span');
                icon.append(iconSymbol);
                icon.style.fontFamily = 'FontAwesome';
                icon.style.fontSize = size + 'px';
                icon.style.color = color?.toHexString();
                newElem.append(icon);

                mapboxMarker = new mapboxgl.Marker({ element: newElem });
            } else {
                mapboxMarker = new mapboxgl.Marker({ color: color?.toHexString() });
            }

            mapboxMarker.setLngLat([long, lat])
                .setRotation(rotation)
                .addTo(map);

            if (typeof onClick === 'function') {
                mapboxMarker.getElement().addEventListener('click', () => {
                    onClick(CloudObject.get(marker.key));
                });
            }
            handlers.set(key, mapboxMarker);

            if (popupText !== '') {
                mapboxMarker.setPopup(new mapboxgl.Popup({ offset: 25 }).setText(popupText));
            }
        }
    }



    /**
     * Enable or disable controls on the map
     *
     * @param interactive
     * @param map
     */
    handleInteractivity(interactive, map) {
        if (interactive) {
            map['scrollZoom'].enable();
            map['scrollZoom'].enable();
            map['boxZoom'].enable();
            map['dragRotate'].enable();
            map['dragPan'].enable();
            map['keyboard'].enable();
            map['doubleClickZoom'].enable();
            map['touchZoomRotate'].enable();
        } else {
            map['scrollZoom'].disable();
            map['scrollZoom'].disable();
            map['boxZoom'].disable();
            map['dragRotate'].disable();
            map['dragPan'].disable();
            map['keyboard'].disable();
            map['doubleClickZoom'].disable();
            map['touchZoomRotate'].disable();
        }
    }



    /**
     * Set the camera on the map
     *
     * @param {BrickContext} $
     * @param {CloudObject} gpsCoord
     * @param {*} map
     */
    handleCamera($, gpsCoord, map) {
        combineLatest([
            gpsCoord.observe($, MapboxCamera.latitudeProp),
            gpsCoord.observe($, MapboxCamera.longitudeProp),
            gpsCoord.observe($, MapboxCamera.altitudeProp),
            gpsCoord.observe($, MapboxCamera.pitchProp),
            gpsCoord.observe($, MapboxCamera.bearingProp)
        ]).subscribe(([lat, long, alt, pitch, bearing]) => {
            const camera = map.getFreeCameraOptions();
            camera.position = mapboxgl.MercatorCoordinate.fromLngLat(
                {
                    lng: long,
                    lat: lat
                },
                alt
            );

            camera.setPitchBearing(pitch, bearing);
            map.setFreeCameraOptions(camera);
        });
    }



    /**
     * Add the 3D effect on the map
     *
     * @param context
     * @param map
     */
    renderTerrain(context, map) {
        const removeTerrain = () => {
            if (map.getSource('mapbox-dem')) {
                map.setTerrain();
                map.removeSource('mapbox-dem');
                map.removeLayer('sky');
            }
        }

        if (context.get('withTerrain')) {
            removeTerrain();
            if (map.isStyleLoaded()) {
                map.addSource('mapbox-dem', {
                    'type': 'raster-dem',
                    'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
                    'tileSize': 512,
                    'maxzoom': 14
                });

                map.setTerrain({ 'source': 'mapbox-dem', 'exaggeration': 1 });

                map.addLayer({
                    'id': 'sky',
                    'type': 'sky',
                    'paint': {
                        'sky-type': 'atmosphere',
                        'sky-atmosphere-sun': [0.0, 0.0],
                        'sky-atmosphere-sun-intensity': 15
                    }
                });
            }
        } else {
            removeTerrain();
        }
    }

}

registerBrick('017c3c32805f61ce7ef2', MapboxMap);
